import React, { useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import API from '../../../../services/API'
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  Divider,
  Grid,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
  LinearProgress,
  Box,
  Card,
  CardContent,
  CardHeader,
  FormControlLabel,
} from '@mui/material'
import AnDateField from '../../../../components/DateTime/DateField'
import StaffSelector from '../../../events/StaffSelector'
import safetyCheckQuestions from './safetyCheckQuestions'
import { GlobalContext, UserContext } from '../../../../App'

// Also the amend form: mounted at /fleet/vehicle-safety-check/:reportID/edit
// it loads the recorded check and saves over it (Fleet team only).
const NewSafetyCheck = () => {
  const { reportID } = useParams()
  const editing = !!reportID
  const [loadingReport, setLoadingReport] = useState(editing)
  const [reportError, setReportError] = useState(false)
  const [savedAnswers, setSavedAnswers] = useState(null)
  const [saving, setSaving] = useState(false)
  const [loadingVehicleOptions, setLoadingVehicleOptions] = useState(true)
  const [vehicleOptions, setVehicleOptions] = useState([])
  const [selectedVehicle, setSelectedVehicle] = useState(null)
  const [preSelectedVehicle, setPreSelectedVehicle] = useState(false)

  const [safetyCheckDate, setSafetyCheckDate] = useState(moment())
  const [vehicleMileage, setVehicleMileage] = useState('')

  const [testedByEmployee, setTestedByEmployee] = useState(true)
  const [testedByEmployeeValue, setTestedByEmployeeValue] = useState(null)
  const [testedByExternalValue, setTestedByExternalValue] = useState({
    garageName: '',
    technicianName: '',
    invoiceNumber: '',
  })

  const [companySettings, setCompanySettings] = useState({})
  const [baseQuestions, setQuestions] = useState(safetyCheckQuestions)
  const [answers, setAnswers] = useState({})
  const [comments, setComments] = useState('')

  const { setToast } = useContext(GlobalContext)
  const { user } = useContext(UserContext)
  const canAmend = (user?.employee?.teams ?? []).includes('Fleet')

  // When amending, keep every item the check was recorded with on the form,
  // even if the organisation's question set has changed since.
  const questions = useMemo(() => {
    if (!savedAnswers) return baseQuestions
    const merged = {}
    for (const category of Object.keys(baseQuestions)) {
      merged[category] = [...baseQuestions[category]]
    }
    for (const category of Object.keys(savedAnswers)) {
      const items = merged[category] ?? (merged[category] = [])
      for (const item of Object.keys(savedAnswers[category] ?? {})) {
        if (!items.includes(item)) items.push(item)
      }
    }
    return merged
  }, [baseQuestions, savedAnswers])

  // ---------------------------------------------
  // Load settings
  // ---------------------------------------------
  useEffect(() => {
    API.get('/settings?select=vehicleSafetyCheckFrequency').then((res) => {
      if (res.status === 200) setCompanySettings(res.data)
    })
  }, [])

  // ---------------------------------------------
  // Parse vehicle ID from URL
  // ---------------------------------------------
  const useQuery = () => {
    const { search } = useLocation()
    return useMemo(() => new URLSearchParams(search), [search])
  }
  const vehicleID = useQuery().get('vehicle')

  // ---------------------------------------------
  // Load vehicles
  // ---------------------------------------------
  const fetchVehicleOptions = () => {
    API.get('/vehicles')
      .then((res) => {
        if (res.status === 200) setVehicleOptions(res.data)
      })
      .catch(() =>
        setToast({
          message: 'Unable to load vehicles. Please contact support',
          severity: 'error',
        })
      )
  }

  useEffect(() => {
    if (editing) {
      API.get(`/fleet/vehicle-safety-check-report/${reportID}`)
        .then((res) => {
          if (res.status !== 200 || !res.data) return setReportError(true)
          const report = res.data
          setSelectedVehicle(report.vehicle)
          setPreSelectedVehicle(true)
          setLoadingVehicleOptions(false)
          setSafetyCheckDate(moment(report.testDate))
          setVehicleMileage(report.mileage ?? '')
          setTestedByEmployee(!!report.testedByEmployee)
          setTestedByEmployeeValue(report.testedByEmployee ?? null)
          setTestedByExternalValue({
            garageName: report.testedBy?.garageName ?? '',
            technicianName: report.testedBy?.technicianName ?? '',
            invoiceNumber: report.testedBy?.invoiceNumber ?? '',
          })
          setAnswers(report.answers ?? {})
          setSavedAnswers(report.answers ?? {})
          setComments(report.additionalComments ?? '')
        })
        .catch(() => setReportError(true))
        .finally(() => setLoadingReport(false))
    } else if (vehicleID) {
      API.get(`/vehicle/${vehicleID}`)
        .then((res) => {
          if (res.status === 200) {
            setSelectedVehicle(res.data.vehicle)
            setPreSelectedVehicle(true)
            setLoadingVehicleOptions(false)
          }
        })
        .catch(() => fetchVehicleOptions())
    } else {
      fetchVehicleOptions()
    }
  }, [])

  // ---------------------------------------------
  // Load custom questions (if any)
  // ---------------------------------------------
  useEffect(() => {
    API.get('/settings?select=vehicleSafetyCheckCustomQuestions').then(
      (res) => {
        if (res.status === 200 && res.data.vehicleSafetyCheckCustomQuestions) {
          setQuestions(res.data.vehicleSafetyCheckCustomQuestions)
        }
      }
    )
  }, [])

  // ---------------------------------------------
  // Safe update answer object
  // ---------------------------------------------
  const setAnswerValue = (category, item, field, value) => {
    // Update answers
    setAnswers((prev) => {
      const current = prev[category]?.[item] || {}

      let updatedItem = {
        ...current,
        [field]: value,
      }

      // ONLY auto-clear rule:
      // 🔥 If N/A is selected → everything else is cleared
      if (field === 'notApplicable' && value === true) {
        updatedItem = {
          notApplicable: true,
          safe: false,
          newPartFitted: false,
          advised: false,
          comments: current.comments || '',
        }
      }

      // If N/A is unchecked → nothing else clears
      if (field === 'notApplicable' && value === false) {
        updatedItem.notApplicable = false
        // Leave all others unchanged
      }

      return {
        ...prev,
        [category]: {
          ...(prev[category] || {}),
          [item]: updatedItem,
        },
      }
    })
  }

  const pageTitle = `${editing ? 'Amend ' : ''}Vehicle Safety Check ${
    selectedVehicle ? `for ${selectedVehicle.callSign}` : ''
  }`

  const navigate = useNavigate()

  const handleSubmit = ({
    safeCount,
    naCount,
    partCount,
    advisoryCount,
    totalAnswered,
  }) => {
    const dataToUpload = {
      vehicle: selectedVehicle._id,
      testDate: safetyCheckDate.toString(),
      mileage: vehicleMileage,
      additionalComments: comments,
      answers,
      testStats: {
        safeCount,
        naCount,
        partCount,
        advisoryCount,
        totalAnswered,
      },
    }
    if (testedByEmployee) {
      dataToUpload.testedByEmployee = testedByEmployeeValue._id
    } else {
      dataToUpload.testedBy = testedByExternalValue
    }

    // API never rejects on 4xx/5xx, so failures are handled on the status.
    const failed = (res) =>
      setToast({
        message:
          res?.data?.error ||
          (editing
            ? 'Unable to save changes to this Safety Check.'
            : 'Unable to submit Safety Check.'),
        severity: 'error',
      })

    setSaving(true)
    const request = editing
      ? API.put(`/fleet/vehicle-safety-check/${reportID}`, dataToUpload)
      : API.post('/fleet/vehicle-safety-check', dataToUpload)
    request
      .then((res) => {
        if (res.status !== 200) return failed(res)
        navigate(
          editing
            ? `/fleet/vehicle-safety-check/${reportID}`
            : `/fleet/${selectedVehicle._id}?opentab=safety`,
        )
        setToast({
          message: editing ? 'Safety Check Updated' : 'Safety Check Recorded',
        })
      })
      .catch(() => failed())
      .finally(() => setSaving(false))
  }

  if (editing && !canAmend)
    return (
      <Alert severity="warning">
        Only members of the Fleet team can amend a recorded safety check.
      </Alert>
    )

  if (loadingReport) return <LinearProgress />

  if (reportError)
    return <Alert severity="error">Unable to load this safety check.</Alert>

  // ---------------------------------------------
  // UI START
  // ---------------------------------------------
  return (
    <Stack gap={2}>
      <title>{pageTitle}</title>

      {!editing && companySettings?.vehicleSafetyCheckFrequency === 'Disabled' ? (
        <Alert severity="info">
          Vehicle Safety Checks have been disabled for your organisation.
        </Alert>
      ) : (
        <>
          {/* ---------------- HEADER CARD --------------- */}
          <Card elevation={2} sx={{ borderRadius: 3 }}>
            <CardHeader
              title={
                editing ? 'Amend Vehicle Safety Check' : 'New Vehicle Safety Check'
              }
              subheader={
                selectedVehicle
                  ? `Vehicle: ${selectedVehicle.callSign}`
                  : 'Select a vehicle to begin'
              }
              titleTypographyProps={{ variant: 'h5', fontWeight: 700 }}
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6} lg={4}>
                  <Autocomplete
                    value={selectedVehicle}
                    onChange={(e, v) => setSelectedVehicle(v)}
                    getOptionLabel={(v) => v.callSign || ''}
                    disabled={preSelectedVehicle}
                    options={
                      editing && selectedVehicle
                        ? [selectedVehicle]
                        : vehicleOptions
                    }
                    loading={loadingVehicleOptions}
                    renderInput={(params) => (
                      <TextField {...params} label="Vehicle" fullWidth />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                  <AnDateField
                    value={safetyCheckDate}
                    onChange={(newDate) => setSafetyCheckDate(newDate)}
                    label="Safety Check Date"
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                  <TextField
                    label="Mileage"
                    value={vehicleMileage}
                    onChange={(e) => setVehicleMileage(e.target.value)}
                    fullWidth
                    type="number"
                    required
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* ---------------- TESTED BY ---------------- */}
          <Card elevation={1} sx={{ borderRadius: 3 }}>
            <CardHeader title="Tested By" />
            <CardContent>
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={testedByEmployee}
                      onChange={(e) => setTestedByEmployee(e.target.checked)}
                      color="success"
                    />
                  }
                  label={
                    testedByEmployee ? 'Internal Employee' : 'External Garage'
                  }
                />

                {testedByEmployee ? (
                  <StaffSelector
                    value={testedByEmployeeValue}
                    onChange={(e, staff) => setTestedByEmployeeValue(staff)}
                    label="Tested By"
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    fullWidth
                  />
                ) : (
                  <Grid container spacing={1}>
                    <Grid item xs={12} lg={4}>
                      <TextField
                        label="Garage Name"
                        fullWidth
                        value={testedByExternalValue.garageName}
                        onChange={(e) =>
                          setTestedByExternalValue((prev) => ({
                            ...prev,
                            garageName: e.target.value,
                          }))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} lg={4}>
                      <TextField
                        label="Technician Name"
                        fullWidth
                        value={testedByExternalValue.technicianName}
                        onChange={(e) =>
                          setTestedByExternalValue((prev) => ({
                            ...prev,
                            technicianName: e.target.value,
                          }))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} lg={4}>
                      <TextField
                        label="Invoice Number"
                        fullWidth
                        value={testedByExternalValue.invoiceNumber}
                        onChange={(e) =>
                          setTestedByExternalValue((prev) => ({
                            ...prev,
                            invoiceNumber: e.target.value,
                          }))
                        }
                      />
                    </Grid>
                  </Grid>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* ---------------- SAFETY CHECK QUESTIONS ---------------- */}
          <Card elevation={1} sx={{ borderRadius: 3 }}>
            <CardHeader
              title="Safety Checklist"
              subheader="Complete safety-critical items below"
            />
            <CardContent>
              <Box display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => {
                    const updated = {}
                    for (const cat of Object.keys(questions)) {
                      updated[cat] = {}
                      for (const item of questions[cat]) {
                        updated[cat][item] = {
                          safe: true,
                          notApplicable: false,
                          newPartFitted: false,
                          advised: false,
                          comments: '',
                        }
                      }
                    }
                    setAnswers(updated)
                  }}
                >
                  Mark All Items as Safe
                </Button>
              </Box>
              <Stack divider={<Divider />} spacing={3}>
                {Object.keys(questions).map((category) => (
                  <Box key={category}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: 2,
                        color: 'primary.dark',
                      }}
                    >
                      {category}
                    </Typography>

                    <Stack spacing={2}>
                      {questions[category].map((item) => (
                        <Card
                          key={item}
                          variant="outlined"
                          sx={{
                            borderRadius: 2,
                            p: 2,
                            bgcolor: '#fafafa',
                            borderLeft: answers?.[category]?.[item]
                              ?.notApplicable
                              ? '5px solid #9e9e9e'
                              : answers?.[category]?.[item]?.advised
                              ? '5px solid #ff9800'
                              : answers?.[category]?.[item]?.newPartFitted
                              ? '5px solid #2196f3'
                              : answers?.[category]?.[item]?.safe
                              ? '5px solid #4caf50'
                              : '5px solid #e0e0e0',
                          }}
                        >
                          <Grid container spacing={2} alignItems="center">
                            {/* LABEL */}
                            <Grid item xs={12} md={5}>
                              <Typography fontWeight={600}>{item}</Typography>
                            </Grid>

                            {/* CHECKBOXES */}
                            <Grid
                              item
                              xs={12}
                              md={7}
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 2,
                                justifyContent: {
                                  xs: 'flex-start',
                                  md: 'flex-end',
                                },
                              }}
                            >
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={
                                      answers?.[category]?.[item]?.safe || false
                                    }
                                    disabled={
                                      answers?.[category]?.[item]?.notApplicable
                                    }
                                    onChange={(e) =>
                                      setAnswerValue(
                                        category,
                                        item,
                                        'safe',
                                        e.target.checked
                                      )
                                    }
                                  />
                                }
                                label="Safe"
                              />

                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={
                                      answers?.[category]?.[item]
                                        ?.notApplicable || false
                                    }
                                    onChange={(e) =>
                                      setAnswerValue(
                                        category,
                                        item,
                                        'notApplicable',
                                        e.target.checked
                                      )
                                    }
                                  />
                                }
                                label="N/A"
                              />

                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={
                                      answers?.[category]?.[item]
                                        ?.newPartFitted || false
                                    }
                                    disabled={
                                      answers?.[category]?.[item]?.notApplicable
                                    }
                                    onChange={(e) =>
                                      setAnswerValue(
                                        category,
                                        item,
                                        'newPartFitted',
                                        e.target.checked
                                      )
                                    }
                                  />
                                }
                                label="Part Fitted"
                              />

                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={
                                      answers?.[category]?.[item]?.advised ||
                                      false
                                    }
                                    disabled={
                                      answers?.[category]?.[item]?.notApplicable
                                    }
                                    onChange={(e) =>
                                      setAnswerValue(
                                        category,
                                        item,
                                        'advised',
                                        e.target.checked
                                      )
                                    }
                                  />
                                }
                                label="Advisory / Recommended Repair"
                              />
                            </Grid>

                            {/* COMMENT FIELD */}
                            <Grid item xs={12}>
                              <TextField
                                label="Comments / Notes"
                                fullWidth
                                multiline
                                value={
                                  answers?.[category]?.[item]?.comments || ''
                                }
                                onChange={(e) =>
                                  setAnswerValue(
                                    category,
                                    item,
                                    'comments',
                                    e.target.value
                                  )
                                }
                              />
                            </Grid>
                          </Grid>
                        </Card>
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </>
      )}
      <Card elevation={1} sx={{ borderRadius: 3 }}>
        <CardHeader title="Additional Comments" />
        <CardContent>
          <TextField
            multiline
            minRows={3}
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            fullWidth
          />
        </CardContent>
      </Card>
      <Card elevation={1} sx={{ borderRadius: 3 }}>
        <CardHeader title="Summary" />
        <CardContent sx={{ p: 2 }}>
          {(() => {
            let safeCount = 0
            let naCount = 0
            let partCount = 0
            let advisoryCount = 0
            let totalAnswered = 0

            for (const cat of Object.keys(questions)) {
              for (const item of questions[cat]) {
                const a = answers?.[cat]?.[item]

                // If no answer at all → skip
                if (!a) continue

                // Count the specific statuses
                if (a.safe) safeCount++
                if (a.notApplicable) naCount++
                if (a.newPartFitted) partCount++
                if (a.advised) advisoryCount++

                // Count this QUESTION as answered once
                if (a.safe || a.notApplicable || a.newPartFitted || a.advised) {
                  totalAnswered++
                }
              }
            }

            return (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} lg={4}>
                  <Card
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Total Answered
                    </Typography>
                    <Typography variant="h5" fontWeight={700}>
                      {totalAnswered}
                    </Typography>
                  </Card>
                </Grid>

                <Grid item xs={6} sm={3} lg={2}>
                  <Card
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      borderLeft: '5px solid #4caf50',
                    }}
                  >
                    <Typography variant="body2" color="success.main">
                      Safe
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      color="success.main"
                    >
                      {safeCount}
                    </Typography>
                  </Card>
                </Grid>

                <Grid item xs={6} sm={3} lg={2}>
                  <Card
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      borderLeft: '5px solid #9e9e9e',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      N/A
                    </Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {naCount}
                    </Typography>
                  </Card>
                </Grid>

                <Grid item xs={6} sm={3} lg={2}>
                  <Card
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      borderLeft: '5px solid #2196f3',
                    }}
                  >
                    <Typography variant="body2" color="info.main">
                      Part Fitted
                    </Typography>
                    <Typography variant="h6" fontWeight={700} color="info.main">
                      {partCount}
                    </Typography>
                  </Card>
                </Grid>

                <Grid item xs={6} sm={3} lg={2}>
                  <Card
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      borderLeft: '5px solid #ff9800',
                    }}
                  >
                    <Typography variant="body2" color="warning.main">
                      Advisory
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      color="warning.main"
                    >
                      {advisoryCount}
                    </Typography>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <Button
                    fullWidth
                    color="success"
                    variant="contained"
                    onClick={() =>
                      handleSubmit({
                        safeCount,
                        naCount,
                        partCount,
                        advisoryCount,
                        totalAnswered,
                      })
                    }
                    disabled={
                      saving ||
                      !selectedVehicle ||
                      (testedByEmployee && !testedByEmployeeValue) ||
                      totalAnswered === 0 ||
                      !vehicleMileage
                    }
                  >
                    {editing ? 'Save Changes' : 'Submit'}
                  </Button>
                </Grid>
              </Grid>
            )
          })()}
        </CardContent>
      </Card>
    </Stack>
  )
}

export default NewSafetyCheck
